import React, { useEffect, useState, Fragment } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import GridContainer from 'components/GridContainer';
import PageContainer from 'components/PageContainer';
import { TrancheVisibility } from 'further-types/fund';
import { TagName } from 'further-types/tag';
import FeeManagement from './FeeManagement';
import BasicInformation from './BasicInformation';
import TrancheDataSnapshot from './TrancheDataSnapshot';
import TranchePromotionInformation from './TrancheInvestmentDocumentation';
import BankDetails from './BankDetails';
import FinalDealToggleAndLog from './FinalDealToggleAndLog';
import AdditionalInformation from './AdditionalInformation';
import ConfirmationEmailCustomisation from './ConfirmationEmailCustomisation';
import AccordionItem from './AccordionItem';
import { useGetRole } from 'hooks/ui/useGetRole';
import { isValidEmail } from 'further-ui/utils';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { useTags } from 'hooks/data/tag/useTags';
import { useFund } from 'hooks/data/fund/useFund';
import PageContent from 'components/PageContent';
import Banner from './Banner';
import api from 'lib/trpcClient';
import Button from 'components/Button';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import AdHocAdjustmentTooltip from './AdHocAdjustmentTooltip';
import useListPendingAdvisedForFund from 'hooks/data/investment/useListPendingAdvisedForFund';
import useReassignOrDeletePendingAdvised from 'hooks/data/investment/useReassignOrDeletePendingAdvised';
import { FundStatus } from 'adminConstants/fundProperties';
import TrancheSelector from 'components/TrancheSelector';
import { useFunds } from 'hooks/data/fund/useFunds';
import { Link } from 'react-router-dom';
import { SUBSCRIPTION_FUNNEL } from 'adminConstants/routes';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

export type TranchePromotion = {
  applicationFormFile: { path: string; name: string; preview: string } | null;
  prospectusFile: { path: string; name: string; preview: string } | null;
  keyInfoFile: { path: string; name: string; preview: string } | null;
};

const StyledCloseTrancheModalSubtitle = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledSubscriptionFunnelLink = styled(Link)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.green,
}));

const useStyles = makeStyles()(() => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  inlineContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
}));

const AddUpdateTranche: React.FC = () => {
  const history = useHistory();
  const { firmId: usersFirmId } = useGetRole();
  const [updatePermission] = useHasPermissions([RolePermission.EditTranche]);
  const { error: showError } = useNotification();
  const { uploadFile } = useFileUpload();
  const { id: idInUrl } = useParams<{ id?: string }>();
  const [id, setId] = useState(idInUrl ?? '');
  const [isSyndicate, setIsSyndicate] = useState(false);
  const { get, edit, create } = useFund(id);
  const updateFeeManagement = useDisclosure();

  const hasPrerequisitesForPublishing =
    get.data &&
    !!get.data?.firmBankDetailId &&
    (get.data?.tag?.isSyndicate || !!get.data?.applicationFormFile);
  const isDraft = get.data?.fundStatus === 2;

  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Tranches', link: '/tranche' },
    {
      label: idInUrl ? 'Update Tranche' : 'Add Tranche',
      link: '/',
      isActive: true,
    },
  ];

  const { tags } = useTags();
  const [expanded, setExpanded] = useState('basicInformation');
  const [loading, setLoading] = useState(true);
  const { classes } = useStyles();
  const [errorTextBasic, setErrorTextBasic] = useState({});
  const [errorTextKeyData, setErrorTextKeyData] = useState({});

  const [tagName, setTagName] = useState();
  const [bank, setBank] = useState<{
    firmBankDetailId?: string;
    title?: string;
    customBankTransferReference?: string;
  }>({
    firmBankDetailId: undefined,
    title: undefined,
    customBankTransferReference: undefined,
  });
  const [bankError, setBankError] = useState<Record<string, string>>({});
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [hasKiCertificate, setHasKiCertificate] = useState(false);
  const [additionalInfoError, setAdditionalInfoError] = useState('');

  const [value, setValue] = useState<{
    firmId: string;
    fundName: string;
    fundStatus: number;
    startDate: Date | null;
    finishDate: Date | null;
    closedDate: Date | null;
    tag: string;
    isProfessionalClientsOnly: boolean;
    trancheVisibility: TrancheVisibility;
    fundRaiseLimit: number | null;
  }>({
    firmId: usersFirmId || '',
    fundName: '',
    fundStatus: 2,
    startDate: null,
    finishDate: null,
    closedDate: null,
    tag: '',
    isProfessionalClientsOnly: false,
    trancheVisibility: TrancheVisibility.ShowOnFirm,
    fundRaiseLimit: null,
  });
  const [keyData, setKeyData] = useState({
    incomeTaxRebate: '',
    targetReturn: '',
    targetPortfolioSize: '',
    minimumDirectInvestment: '',
    minimumAdvisedInvestment: '',
    minimumIncludesAdviceFee: false,
  });
  const [tranchePromotion, setTranchePromotion] = useState<TranchePromotion>({
    applicationFormFile: null,
    keyInfoFile: null,
    prospectusFile: null,
  });
  const [additionalDocuments, setAdditionalDocuments] = useState<
    Array<{
      name: string;
      path: string;
      preview: string;
    }>
  >([]);
  const [investment, setInvestment] = useState([]);
  const [feeManagement, setFeeManagement] = useState<FeeManagement>({
    key: 'feeManagement',
    defaultCoreFee: '',
    defaultPerformanceFee: '',
  });
  const closeTrancheDisclosure = useDisclosure<{
    requestPayload: Record<string, unknown>;
    targetFundId?: string | null;
  }>();
  const pendingAdvised = useListPendingAdvisedForFund(id);
  const reassignOrDeleteMutation = useReassignOrDeletePendingAdvised();
  const { firmId } = useGetRole();
  const { tranches } = useFunds({ firmId: value.firmId || firmId });

  const [confirmationEmailCustomisation, setConfirmationEmailCustomisation] =
    useState<{
      whatToExpectNow?: string;
      additionalAttachment: {
        path: string;
        name: string;
        preview: string;
      } | null;
    }>({
      additionalAttachment: null,
    });

  const handleInputChangeAdditional = (e) => {
    const { value } = e.target;
    setAdditionalInfo(value);
  };

  const validateBasicInformation = () => {
    const errors: Record<string, string> = {};
    let isError = false;
    if (!value.firmId) {
      errors.firmId = 'Please select firm.';
      isError = true;
    }
    if (!value.fundName) {
      errors.fundName = 'Please enter tranche name.';
      isError = true;
    }
    if (!value.finishDate) {
      errors.finishDate = 'Please select finish date.';
      isError = true;
    }
    if (!value.tag) {
      errors.tag = 'Please select fund type.';
      isError = true;
    }
    setErrorTextBasic(errors);
    return {
      errors,
      isError,
    };
  };
  const validateKeyData = () => {
    const errors: Record<string, string> = {};
    let isError = false;

    if (!keyData.minimumDirectInvestment) {
      errors.minimumDirectInvestment =
        'Please enter minimum direct subscription.';
      isError = true;
    }

    if (!keyData.minimumAdvisedInvestment) {
      errors.minimumAdvisedInvestment =
        'Please enter minimum advised subscription.';
      isError = true;
    }

    if (!keyData.incomeTaxRebate) {
      errors.incomeTaxRebate = 'Please enter income tax relief.';
      isError = true;
    }

    if (!keyData.targetReturn) {
      errors.targetReturn = 'Please select target return.';
      isError = true;
    }

    if (!keyData.targetPortfolioSize) {
      errors.targetPortfolioSize = 'Please enter target portfolio size.';
      isError = true;
    }

    setErrorTextKeyData(errors);

    return {
      errors,
      isError,
    };
  };

  const handleClick = async (isBannerClick?: boolean) => {
    let response;
    const validateData = validateBasicInformation();
    if (!validateData.isError) {
      const requestPayload = {
        key: 'basic',
        ...value,
        ...(isBannerClick ? { fundStatus: 1 } : {}),
      };

      // Check if closing tranche and if there are pending advised applications
      const isClosingTranche =
        id &&
        requestPayload.fundStatus === FundStatus.CLOSED &&
        get.data?.fundStatus !== FundStatus.CLOSED;

      if (isClosingTranche) {
        // Ensure we have up-to-date data; if not loaded, fetch now
        const pendingData =
          pendingAdvised.data ?? (await pendingAdvised.refetch())?.data;
        if (pendingData && pendingData.count > 0) {
          closeTrancheDisclosure.stageAction({
            requestPayload,
            targetFundId: null,
          });
          return;
        }
      }

      if (id) {
        edit.mutate(requestPayload);
        if (isBannerClick) {
          setValue({ ...value, fundStatus: 1 });
        }
      } else {
        response = await create.mutateAsync(requestPayload);
        setId(response?._id);
      }

      if (!isBannerClick) setExpanded('trancheDataSnapshot');
    }
  };

  const handleConfirmCloseTranche = async () => {
    const { requestPayload, targetFundId } = closeTrancheDisclosure.actionProps;
    if (!id || !requestPayload) return;

    // Reassign or delete pending advised applications
    await reassignOrDeleteMutation.mutateAsync({
      sourceFundId: id,
      targetFundId: targetFundId || null,
    });

    edit.mutate(requestPayload);
    closeTrancheDisclosure.onClose();
  };

  const handelClickAdditional = async () => {
    if (!additionalInfo) {
      setAdditionalInfoError('Please enter notification list.');
    } else {
      const trimmedAdditionalInfo = additionalInfo
        .split(';')
        .map((email) => email.trim());
      const isValid = trimmedAdditionalInfo.every((email) => {
        if (email) {
          return isValidEmail(email);
        }
        return true;
      });
      if (!isValid) {
        setAdditionalInfoError(
          'Separate emails with a semi-colon, e.g. name@domain.com;name2@domain.com',
        );
      } else {
        const value = {
          additionalInfo: trimmedAdditionalInfo.join(';'),
          key: 'additionalInfo',
        };
        edit.mutate(value);
        setAdditionalInfoError('');
        setExpanded('confirmationEmailCustomisation');
      }
    }
  };

  const handleClickKeyData = async () => {
    const validateData = validateKeyData();
    if (!validateData.isError) {
      const requestPayload = {
        key: 'keydata',
        ...keyData,
      };

      edit.mutate(requestPayload);
      setExpanded('trancheInvestmentDocumentation');
    }
  };
  const handleClickTranchePromotion = async () => {
    if (tranchePromotion?.prospectusFile?.name.includes('.com/')) {
      tranchePromotion.prospectusFile.name =
        tranchePromotion?.prospectusFile.name.split('.com/')[1];
    }

    if (tranchePromotion?.applicationFormFile?.name.includes('.com/')) {
      tranchePromotion.applicationFormFile.name =
        tranchePromotion?.applicationFormFile.name.split('.com/')[1];
    }

    if (tranchePromotion?.keyInfoFile?.name.includes('.com/')) {
      tranchePromotion.keyInfoFile.name =
        tranchePromotion?.keyInfoFile.name.split('.com/')[1];
    }

    setTranchePromotion({ ...tranchePromotion });
    const value = {
      key: 'prospectus',
      prospectusFile: tranchePromotion.prospectusFile,
      keyInfoFile: tranchePromotion.keyInfoFile,
      applicationFormFile: tranchePromotion.applicationFormFile,
      additionalDocuments,
    };
    edit.mutate(value);
    setExpanded('feeManagement');
  };

  const handleInputChangeEmailCustomisation = async (name, value) => {
    if (name === 'additionalAttachment') {
      if (!value?.[0]?.path) return;

      const { data, error } = await uploadFile(
        value[0],
        UploadType.FundDocuments,
      );
      if (data) {
        confirmationEmailCustomisation.additionalAttachment = {
          path: data.filePath,
          name: value[0].name,
          preview: data.url,
        };
      } else {
        showError(error.message);
      }
    } else if (name === 'additionalAttachmentRemove') {
      confirmationEmailCustomisation.additionalAttachment = null;
    } else {
      confirmationEmailCustomisation[name] = value;
    }

    setConfirmationEmailCustomisation({ ...confirmationEmailCustomisation });
  };
  const handleClickConfirmationEmailCustomisation = async () => {
    if (
      confirmationEmailCustomisation.additionalAttachment?.path.includes(
        '.com/',
      )
    ) {
      confirmationEmailCustomisation.additionalAttachment.path =
        confirmationEmailCustomisation.additionalAttachment.path.split(
          '.com/',
        )[1];
    }

    edit.mutate({
      key: 'confirmationEmailCustomisation',
      whatToExpectNow: confirmationEmailCustomisation.whatToExpectNow,
      additionalAttachment: confirmationEmailCustomisation?.additionalAttachment
        ? {
            path: confirmationEmailCustomisation.additionalAttachment?.path,
            name: confirmationEmailCustomisation.additionalAttachment?.name,
            preview:
              confirmationEmailCustomisation.additionalAttachment?.preview,
          }
        : null,
    });
    setExpanded('trancheHighlights');
  };

  const handleClickBank = async () => {
    let error = false;
    const errors: Record<string, string> = {};
    if (!bank.firmBankDetailId) {
      error = true;
      errors.firmBankDetailId = 'Please select a payment account.';
    }
    if (!error) {
      edit.mutate({
        key: 'bankDetails',
        firmBankDetailId: bank.firmBankDetailId,
        title: bank.title,
        customBankTransferReference: bank.customBankTransferReference,
      });
      setExpanded('additionalInformation');
      setBankError({});
    } else {
      setBankError(errors);
    }
  };

  const getTranchebyId = async () => {
    //TODO: fix this
    const response = get.data as any;
    if (response) {
      const value = {
        firmId: response?.firmId?._id,
        fundName: response?.fundName,
        fundStatus: response?.fundStatus,
        tag: response?.tag ? response?.tag?._id : '',
        startDate: response?.startDate ? new Date(response.startDate) : null,
        closedDate: response?.closedDate ? new Date(response.closedDate) : null,
        finishDate: response?.finishDate ? new Date(response.finishDate) : null,
        isProfessionalClientsOnly: response?.isProfessionalClientsOnly,
        trancheVisibility: response?.trancheVisibility
          ? response?.trancheVisibility
          : TrancheVisibility.ShowOnFirm,
        fundRaiseLimit: response?.fundRaiseLimit ?? null,
      };

      setValue({ ...value });
      setTagName(response?.tag?.name);
      setIsSyndicate(response.tag?.isSyndicate);
      setHasKiCertificate(response?.hasKiCertificate);
      setKeyData({
        targetReturn: response?.targetReturn,
        targetPortfolioSize: response?.targetPortfolioSize,
        minimumDirectInvestment: response?.minimumDirectInvestment,
        minimumAdvisedInvestment: response?.minimumAdvisedInvestment,
        incomeTaxRebate: response?.incomeTaxRebate,
        minimumIncludesAdviceFee: response?.minimumIncludesAdviceFee,
      });
      setBank({
        firmBankDetailId: response?.firmBankDetailId || '',
        title: response?.title || '',
        customBankTransferReference:
          response?.customBankTransferReference || '',
      });

      setTranchePromotion({
        keyInfoFile: response.keyInfoFile,
        applicationFormFile: response.applicationFormFile,
        prospectusFile: response.prospectusFile,
      });

      setAdditionalDocuments(response?.additionalDocuments || []);
      if (response.additionalInfo) {
        setAdditionalInfo(response?.additionalInfo);
      }
      setInvestment(response.investment);
      setFeeManagement({
        key: 'feeManagement',
        defaultPerformanceFee: response?.performanceFeeTemplateId || '',
        defaultCoreFee: response?.coreFeeTemplateId || '',
      });

      if (response.whatToExpectNow) {
        confirmationEmailCustomisation.whatToExpectNow =
          response.whatToExpectNow;
      }
      setConfirmationEmailCustomisation({
        ...confirmationEmailCustomisation,
        additionalAttachment: response.additionalAttachment,
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idInUrl) {
      getTranchebyId();
    } else {
      setLoading(false);
    }
  }, [id, idInUrl, get.data]);

  const handleChange = (panel) => (_, isExpanded) => {
    if (id) {
      setExpanded(isExpanded ? panel : false);
    }
  };
  const handleCancel = () => {
    history.push('/tranche');
  };
  const handleChangeTag = (event, item) => {
    setValue({ ...value, tag: event });
    setIsSyndicate(item.isSyndicate);
    setTagName(item?.name);
  };
  const handleDateChange = (date) => {
    setValue({ ...value, finishDate: date });
  };

  const onChangeTranchePromotion = async (files, name) => {
    if (!files?.[0]?.path) return;

    const { data, error } = await uploadFile(
      files[0],
      UploadType.FundDocuments,
    );
    if (data) {
      tranchePromotion[name] = {
        path: data.filePath,
        name: files[0].name,
        preview: data.url,
      };
      setTranchePromotion({
        ...tranchePromotion,
      });
    } else {
      showError(error.message);
    }
  };

  const handleChangeAdditionalDocuments = async (files) => {
    if (!files?.[0]?.path) return;

    const filesToUpload: Array<{
      path: string;
      name: string;
      preview: string;
    }> = [];
    let hasError = false;
    for await (const file of files) {
      const { data, error } = await uploadFile(file, UploadType.FundDocuments);
      if (data) {
        filesToUpload.push({
          path: data.filePath,
          name: file.name,
          preview: data.url,
        });
      } else {
        showError(error.message);
        hasError = true;
        break;
      }
    }
    if (!hasError)
      setAdditionalDocuments([...additionalDocuments, ...filesToUpload]);
  };

  const handleRemoveAdditionalDocuments = (index) => {
    setAdditionalDocuments(additionalDocuments.filter((_, i) => i !== index));
  };

  const { data: coreFeeTemplates, refetch: refetchCoreFeeTemplates } =
    api.coreFeeTemplates.list.useQuery(
      {
        orderBy: 'createdAt',
        order: 'desc',
        firmId: value.firmId,
      },
      { refetchOnWindowFocus: true },
    );

  const {
    data: performanceFeeTemplates,
    refetch: refetchPerformanceFeeTemplates,
  } = api.performanceFeeTemplates.list.useQuery(
    {
      orderBy: 'createdAt',
      order: 'desc',
      firmId: value.firmId,
    },
    { refetchOnWindowFocus: true },
  );

  const handleChangeFeeManagement = async () => {
    // If there are investments in tranche, we need to show a warning modal
    if (investment.length > 0) {
      updateFeeManagement.onOpen();
    } else {
      edit.mutate({
        key: 'feeManagement',
        defaultCoreFee: feeManagement.defaultCoreFee,
        defaultPerformanceFee: feeManagement.defaultPerformanceFee,
      });
      setExpanded('bankDetails');
    }
  };

  return (
    <Fragment>
      <PageContainer
        heading={id ? 'Tranche: Tranche details' : 'Add Tranche'}
        breadcrumbs={breadcrumbs}
        loading={loading}
      >
        <PageContent>
          {(isDraft || !get.data) && (
            <Banner
              hasPrerequisitesForPublishing={!!hasPrerequisitesForPublishing}
              handleOpenFund={() => {
                handleClick(true);
              }}
            />
          )}

          <AccordionItem
            expanded={expanded}
            heading="Basic information"
            handleChange={handleChange}
            panelName="basicInformation"
            chip={!id ? 'Required' : undefined}
          >
            <BasicInformation
              errorTextBasic={errorTextBasic}
              handleCancel={handleCancel}
              handleChangeTag={handleChangeTag}
              handleClick={handleClick}
              handleDateChange={handleDateChange}
              id={id}
              investment={investment}
              setValue={setValue}
              tagList={tags.data ?? []}
              tagName={tagName}
              value={value}
              buttonDisabled={edit.isPending || create.isPending}
              hasPrerequisitesForPublishing={
                // considers state instead of fetched data
                !!bank.firmBankDetailId &&
                (isSyndicate ||
                  tagName === TagName.Direct ||
                  !!tranchePromotion?.applicationFormFile)
              }
              isSyndicate={isSyndicate || tagName === TagName.Direct}
              viewOnly={!updatePermission}
            />
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Tranche data snapshot"
            handleChange={handleChange}
            panelName="trancheDataSnapshot"
            disabled={!id}
          >
            <TrancheDataSnapshot
              errorTextKeyData={errorTextKeyData}
              handleCancel={handleCancel}
              disabled={edit.isPending}
              handleClickKeyData={handleClickKeyData}
              id={id}
              keyData={keyData}
              setKeyData={setKeyData}
              tagName={tagName}
              viewOnly={!updatePermission}
            />
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Tranche subscription documentation"
            handleChange={handleChange}
            panelName="trancheInvestmentDocumentation"
            disabled={!id}
            chip={
              !id ||
              (isDraft &&
                !isSyndicate &&
                tagName !== TagName.Direct &&
                !tranchePromotion?.applicationFormFile)
                ? 'Required'
                : undefined
            }
          >
            <TranchePromotionInformation
              handleCancel={handleCancel}
              handleClickTranchePromotion={handleClickTranchePromotion}
              id={id}
              disabled={edit.isPending}
              onChangeTranchePromotion={onChangeTranchePromotion}
              setTranchePromotion={setTranchePromotion}
              isSyndicate={isSyndicate || tagName === TagName.Direct}
              isLP={tagName === TagName.LP}
              tranchePromotion={tranchePromotion}
              additionalDocuments={additionalDocuments || []}
              handleChangeAdditionalDocuments={handleChangeAdditionalDocuments}
              handleRemoveAdditionalDocuments={handleRemoveAdditionalDocuments}
              viewOnly={!updatePermission}
            />
          </AccordionItem>
          <AccordionItem
            expanded={expanded}
            heading="Fee management"
            handleChange={handleChange}
            panelName="feeManagement"
            disabled={!id}
          >
            <GridContainer>
              <Grid size={{ xs: 12 }}>
                <FeeManagement
                  feeManagement={feeManagement}
                  setFeeManagement={setFeeManagement}
                  coreFeeTemplates={coreFeeTemplates?.result ?? []}
                  performanceFeeTemplates={
                    performanceFeeTemplates?.result ?? []
                  }
                  refetchCoreFeeTemplates={refetchCoreFeeTemplates}
                  refetchPerformanceFeeTemplates={
                    refetchPerformanceFeeTemplates
                  }
                  disabled={!updatePermission}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <div className={classes.inlineContainer}>
                  <div>
                    <AdHocAdjustmentTooltip />
                  </div>
                  {updatePermission && (
                    <div className={classes.buttonContainer}>
                      <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.marginLeftBtn}
                        disabled={edit.isPending}
                        color="primary"
                        onClick={handleChangeFeeManagement}
                      >
                        {id ? 'Update' : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
              </Grid>
            </GridContainer>
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Bank details"
            handleChange={handleChange}
            panelName="bankDetails"
            disabled={!id}
            chip={
              !id || (isDraft && !bank.firmBankDetailId)
                ? 'Required'
                : undefined
            }
          >
            <BankDetails
              bank={bank}
              bankError={bankError}
              handleCancel={handleCancel}
              handleClickBank={handleClickBank}
              id={id}
              buttonDisabled={edit.isPending}
              formDisabled={!updatePermission}
              setBank={setBank}
              firmId={value.firmId}
            />
          </AccordionItem>
          <AccordionItem
            expanded={expanded}
            heading="Additional information"
            handleChange={handleChange}
            panelName="additionalInformation"
            disabled={!id}
          >
            <AdditionalInformation
              additionalInfo={additionalInfo}
              additionalInfoError={additionalInfoError}
              handleCancel={handleCancel}
              buttonDisabled={edit.isPending}
              formDisabled={!updatePermission}
              handelClickAdditional={handelClickAdditional}
              handleInputChangeAdditional={handleInputChangeAdditional}
              id={id}
            />
          </AccordionItem>
          <AccordionItem
            expanded={expanded}
            heading="Confirmation email customisation"
            handleChange={handleChange}
            panelName="confirmationEmailCustomisation"
            disabled={!id}
          >
            <ConfirmationEmailCustomisation
              confirmationEmailCustomisation={confirmationEmailCustomisation}
              handleCancel={handleCancel}
              buttonDisabled={edit.isPending}
              formDisabled={!updatePermission}
              handleClickConfirmationEmailCustomisation={
                handleClickConfirmationEmailCustomisation
              }
              handleInputChangeEmailCustomisation={
                handleInputChangeEmailCustomisation
              }
              id={id}
            />
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Final deal"
            handleChange={handleChange}
            panelName="finalDeal"
            disabled={!id}
          >
            {/* This one does not need a "disabled" as it's handled internally by this component. */}
            <FinalDealToggleAndLog
              hasKiCertficate={hasKiCertificate}
              fundId={id}
              viewOnly={!updatePermission}
            />
          </AccordionItem>
          <AlertDialog
            onClose={updateFeeManagement.onClose}
            open={updateFeeManagement.isOpen}
            title="Warning"
            content={
              <>
                <b>Please note:</b> Changes to default fee settings will only
                apply to future subscriptions. To update existing subscriptions
                in this tranche, please navigate to individual subscription
                pages and edit the selected fee preset
              </>
            }
            btnLabels={{ cancel: 'Go back', confirm: 'Update' }}
            cancelBtnProps={{ color: 'primary', variant: 'outlined' }}
            confirmBtnProps={{
              color: 'primary',
            }}
            onConfirm={() => {
              edit.mutate({
                key: 'feeManagement',
                defaultCoreFee: feeManagement.defaultCoreFee,
                defaultPerformanceFee: feeManagement.defaultPerformanceFee,
              });
              updateFeeManagement.onClose();
              setExpanded('bankDetails');
            }}
          />
          <AlertDialog
            open={closeTrancheDisclosure.isOpen}
            onClose={closeTrancheDisclosure.onClose}
            title="Live advised applications"
            content={
              <>
                <StyledCloseTrancheModalSubtitle>
                  There are pending advised applications for this tranche which
                  you can view on the{' '}
                  <StyledSubscriptionFunnelLink
                    to={SUBSCRIPTION_FUNNEL}
                    target="_blank"
                  >
                    Subscription Funnel
                  </StyledSubscriptionFunnelLink>{' '}
                  page. Select a new tranche for these applications below. If no
                  tranche is selected, the system will delete pending
                  applications.
                </StyledCloseTrancheModalSubtitle>
                <TrancheSelector
                  firmId={value.firmId}
                  openOnly
                  selectedTranche={
                    closeTrancheDisclosure.actionProps.targetFundId &&
                    tranches.data
                      ? tranches.data.find(
                          ({ _id }) =>
                            _id ===
                            closeTrancheDisclosure.actionProps.targetFundId,
                        ) ?? null
                      : null
                  }
                  onChange={(tranche) => {
                    closeTrancheDisclosure.stageAction({
                      ...closeTrancheDisclosure.actionProps,
                      targetFundId: tranche?._id ?? null,
                    });
                  }}
                />
              </>
            }
            btnLabels={{
              cancel: 'Go back',
              confirm: 'Close tranche',
            }}
            onConfirm={handleConfirmCloseTranche}
            confirmBtnProps={{
              disabled:
                reassignOrDeleteMutation.isPending ||
                edit.isPending ||
                !pendingAdvised.data,
              loading: reassignOrDeleteMutation.isPending || edit.isPending,
            }}
          />
        </PageContent>
      </PageContainer>
    </Fragment>
  );
};
export default AddUpdateTranche;
